import { sleep } from '@eluve/utils';

import type {
  MsgToMain,
  WorkletMsgFromMain,
  WorkletMsgToMain,
} from '../workers/messages-types';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore Import worklet URL - works in both Vite and Webpack
import audioWorkletUrl from '../workers/pcm-capture.worklet?worker&url';

import {
  AudioSource,
  AudioSourceConfig,
  AudioSourceInitResult,
} from './audio-source';

const IDEAL_AUDIO_SAMPLE_RATE = 48_000;
const WORKLET_FLUSH_TIMEOUT = 5000;

/**
 * PCM Audio Source - Captures raw PCM audio using AudioWorklet
 * Encodes to Opus using MediaBunny in the worker
 */
export class PcmAudioSource extends AudioSource {
  private audioWorkletNode: AudioWorkletNode | null = null;

  constructor(config: AudioSourceConfig) {
    super(config);
  }

  getMode(): 'pcm' {
    return 'pcm';
  }

  async initialize(): Promise<AudioSourceInitResult> {
    this.logger('info', 'Initializing PCM audio source (AudioWorklet mode)');

    // Create AudioContext with ideal sample rate for Opus
    this.audioContext = new AudioContext({
      sampleRate: IDEAL_AUDIO_SAMPLE_RATE,
    });

    if (this.audioContext.sampleRate !== IDEAL_AUDIO_SAMPLE_RATE) {
      this.logger('warn', 'AudioContext sample rate mismatch', {
        requestedSampleRate: IDEAL_AUDIO_SAMPLE_RATE,
        actualSampleRate: this.audioContext.sampleRate,
      });
    }

    // Load AudioWorklet module
    await this.audioContext.audioWorklet.addModule(audioWorkletUrl);

    // Create AudioWorkletNode
    this.audioWorkletNode = new AudioWorkletNode(
      this.audioContext,
      'pcm-capture',
      {
        numberOfInputs: 1,
        numberOfOutputs: 1,
        outputChannelCount: [this.channelCount],
        processorOptions: {
          channels: this.channelCount,
          blockFrames: 2048,
        },
      },
    );

    // Wait for worklet to signal it's ready before connecting audio source
    await new Promise<void>((resolve) => {
      const readyHandler = (e: MessageEvent<WorkletMsgToMain>) => {
        if (e.data.type === 'worklet-ready') {
          this.logger('info', 'AudioWorkletNode is ready');
          this.audioWorkletNode?.port.removeEventListener(
            'message',
            readyHandler,
          );
          resolve();
        }
      };
      this.audioWorkletNode?.port.addEventListener('message', readyHandler);
      this.audioWorkletNode?.port.start();
    });

    // Connect audio graph
    const src = this.audioContext.createMediaStreamSource(this.mediaStream);
    this.audioWorkletNode.connect(this.audioContext.destination);
    src.connect(this.audioWorkletNode);

    // Set up mute detection (reuses existing AudioContext)
    this.setupMuteDetection(this.audioContext);

    // Forward PCM data from worklet to worker
    this.audioWorkletNode.port.onmessage = (
      ev: MessageEvent<WorkletMsgToMain>,
    ) => {
      const msg = ev.data;
      if (msg.type === 'pcm') {
        const transfers: Transferable[] = [msg.left];
        if (msg.right) transfers.push(msg.right);
        this.postMessage(
          {
            type: 'pcm',
            channels: msg.channels,
            timestamp: msg.timestamp,
            left: msg.left,
            right: msg.right,
          },
          transfers,
        );
      }
    };

    // Listen for encoded audio from worker
    this.worker.addEventListener('message', this.workerMessageHandler);

    return {
      sampleRate: this.audioContext.sampleRate,
      channels: this.channelCount as 1 | 2,
    };
  }

  /**
   * Handle messages from the worker (encoded audio)
   */
  private workerMessageHandler = (e: MessageEvent<MsgToMain>): void => {
    const msg = e.data;
    if (msg.type === 'audio-available') {
      // Create blob from encoded audio
      const blob = new Blob([msg.data], { type: 'audio/webm; codecs=opus' });

      // Publish to subject for live transcription
      this.audioPacketSubject.next({
        timestamp: msg.timestamp,
        data: blob,
      });

      // Report bytes received to parent actor
      this.onBytesReceived(msg.data.byteLength);
    }
  };

  async stop(): Promise<void> {
    this.logger('info', 'Stopping PCM audio source');

    // Remove worker message listener
    this.worker.removeEventListener('message', this.workerMessageHandler);

    // Give a buffer for in-flight audio to drain through the pipeline
    await sleep(150);

    // Wait for worklet to complete flushing
    let flushCompleted = false;
    const workletFlushed = new Promise<void>((resolve) => {
      const flushCompleteHandler = (e: MessageEvent<WorkletMsgToMain>) => {
        if (e.data.type === 'flush-complete') {
          this.logger('info', 'Worklet flush completed');
          this.audioWorkletNode?.port.removeEventListener(
            'message',
            flushCompleteHandler,
          );
          flushCompleted = true;
          resolve();
        }
      };
      this.audioWorkletNode?.port.addEventListener(
        'message',
        flushCompleteHandler,
      );
    });

    try {
      this.audioWorkletNode?.port.postMessage({
        type: 'flush',
      } satisfies WorkletMsgFromMain);

      await Promise.race([
        workletFlushed,
        sleep(WORKLET_FLUSH_TIMEOUT).then(() => {
          if (!flushCompleted) {
            this.logger('warn', 'Worklet flush timeout exceeded');
          }
        }),
      ]);
    } catch (e) {
      this.logger('error', 'Error flushing worklet', {
        error: e,
      });
    }

    // Tear down the audio graph
    try {
      this.audioWorkletNode?.disconnect();
    } catch (e) {
      this.logger('error', 'Error disconnecting AudioWorkletNode', {
        error: e,
      });
    }

    this.audioWorkletNode = null;

    // Close AudioContext
    try {
      await this.audioContext?.close();
    } catch (e) {
      this.logger('error', 'Error closing AudioContext', {
        error: e,
      });
    }

    this.audioContext = null;

    // Clean up mute detection
    await this.cleanupMuteDetection();

    this.logger('info', 'PCM audio source stopped');
  }
}
